import { evictMediaCollectionResponse } from '@apps/admin/routes/WebsiteOverview/routes/Photos/utils/evictMediaCollectionCache.utils';
import {
  UploadPhotoFromUrlPayload,
  useAddMediaToCollectionMutation,
  useCreateMediaPhotoMutation,
  useUploadPhotoFromUrlMutation,
  useUploadPhotosFromUrlsMutation,
} from '@graphql/generated';
import { useFilestack } from '@shared/utils/filestack';
import { WarningTriangle } from '@withjoy/joykit/icons';
import { useCallback, useState } from 'react';
import toast from 'react-hot-toast';
import React from 'react';
import { useTranslation } from '@shared/core';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useFeatureValue } from '@shared/core/featureFlags';
import { isValidPhotoAlbumMultiPhotoUploadPayload } from '@apps/admin/routes/WebsiteOverview/routes/Photos/utils/featureFlagPayload.utils';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useWebsiteOverviewTelemetry } from '@apps/admin/routes/WebsiteOverview/WebsiteOverview.telemetry';

type UseFileUploadJobProps = {
  eventId: string;
  collectionId?: string;
  photoUploadStatusUpdate: (isUploading: boolean, imageCount: number) => void;
};

const useFileUploadJob = (props: UseFileUploadJobProps) => {
  const { eventId, collectionId, photoUploadStatusUpdate } = props;
  const [addMediaToCollection] = useAddMediaToCollectionMutation();
  const [createMediaPhoto] = useCreateMediaPhotoMutation();
  const telemetry = useWebsiteOverviewTelemetry();
  const { eventHandle } = useEventInfo();

  const fileUploadJob = useEventCallback(async (photoId: string) => {
    const { data } = await createMediaPhoto({
      variables: {
        payload: {
          eventId,
          photoId,
        },
      },
      ...(!collectionId && { refetchQueries: ['GetEventMedia'], awaitRefetchQueries: true }),
      update: cache => {
        if (eventHandle) {
          evictMediaCollectionResponse({ cache, eventHandle: eventHandle });
        }
      },
    });
    const mediaId = data?.createMediaPhoto?.id;
    if (mediaId) {
      // if no collectionId, then no need to add media to collection
      if (collectionId) {
        telemetry.uploadAlbumPhoto(eventId, collectionId, mediaId, photoId, data.createMediaPhoto?.photo.url || '');
        await addMediaToCollection({
          variables: {
            payload: {
              collectionId,
              mediaId,
            },
          },
          refetchQueries: ['MediaCollections', 'MediaCollectionById', 'GetEventMedia'],
          awaitRefetchQueries: true,
        });
      }
      photoUploadStatusUpdate(false, 0);
    }
  });

  return { fileUploadJob };
};

type UseWebsitePhotoUploadProps = {
  eventId: string;
  containerId: string;
  collectionId?: string;
  updateUploadStatus?: (isUploading: boolean) => void;
};

export const useWebsitePhotoUpload = (props: UseWebsitePhotoUploadProps) => {
  const { eventId, containerId, collectionId, updateUploadStatus } = props;
  const { t } = useTranslation('websiteOverview');
  const photosTranslationFn = t('photos');
  const { eventHandle } = useEventInfo();
  const telemetry = useWebsiteOverviewTelemetry();

  const [uploadPhotoFromUrl] = useUploadPhotoFromUrlMutation();
  const [uploadPhotosFromUrls] = useUploadPhotosFromUrlsMutation();
  const [createMediaPhoto] = useCreateMediaPhotoMutation();
  const [addMediaToCollection] = useAddMediaToCollectionMutation();

  const { value: photoAlbumMultiPhotoUploadValue, payload: photoAlbumMultiPhotoUploadPayload } = useFeatureValue('photoAlbumMultiPhotoUploadEnabled');
  const photoAlbumMultiPhotoUploadEnabled = photoAlbumMultiPhotoUploadValue === 'on';
  const maxFiles = photoAlbumMultiPhotoUploadEnabled
    ? isValidPhotoAlbumMultiPhotoUploadPayload(photoAlbumMultiPhotoUploadPayload)
      ? photoAlbumMultiPhotoUploadPayload.maxFiles
      : 5
    : 1;

  const [fileUploadStatusData, setFileUploadStatusData] = useState<{
    isUploading: boolean;
    imageCount: number;
  }>({ isUploading: false, imageCount: 0 });

  const photoUploadStatusUpdate = useCallback(
    (isUploading: boolean, imageCount: number) => {
      setFileUploadStatusData({ isUploading, imageCount });
      updateUploadStatus?.(isUploading);
    },
    [updateUploadStatus],
  );

  const { fileUploadJob } = useFileUploadJob({
    eventId,
    photoUploadStatusUpdate,
  });

  const {
    open: openFilePicker,
    getPhotoPreviewUrl,
    getPhotosPrewiewUrls,
  } = useFilestack({
    containerId,
    accept: 'image/*',
    maxFiles,
    transformations: {
      crop: true,
      rotate: true,
    },
    onUploadDone: async (res): Promise<void> => {
      if (res.filesFailed.length) {
        toast(`${photosTranslationFn.savePhotoError()}`, { icon: <WarningTriangle /> });
        openFilePicker();
        return;
      }
      photoUploadStatusUpdate(true, photoAlbumMultiPhotoUploadEnabled ? res.filesUploaded.length : 1);

      if (photoAlbumMultiPhotoUploadEnabled) {
        const previewUrls = await getPhotosPrewiewUrls({ urls: res.filesUploaded.map(file => file.url) });
        const payload: UploadPhotoFromUrlPayload[] = res.filesUploaded.map((file, index) => ({ assetId: file.filename, containerId, url: previewUrls[index] }));
        try {
          const { data, errors } = await uploadPhotosFromUrls({ variables: { payload } });
          if (errors) {
            toast(`${photosTranslationFn.savePhotoError()}`, { icon: <WarningTriangle /> });
            photoUploadStatusUpdate(false, 0);
          }

          const photoDataArray = data?.uploadPhotosFromUrls || [];
          await Promise.all(photoDataArray.map(photoData => photoData?.id && fileUploadJob(photoData.id)))
            .catch(() => {
              toast(`${photosTranslationFn.savePhotoError()}`, { icon: <WarningTriangle /> });
            })
            .finally(() => {
              photoUploadStatusUpdate(false, 0);
            });
        } catch (e) {
          toast(`${photosTranslationFn.savePhotoError()}`, { icon: <WarningTriangle /> });
          photoUploadStatusUpdate(false, 0);
        }
      } else {
        // TODO: clean this below code once the multi photo upload is enabled
        const { filename, url } = res.filesUploaded[0];
        const previewUrl = await getPhotoPreviewUrl({ url });

        try {
          const { data } = await uploadPhotoFromUrl({
            variables: {
              payload: {
                assetId: filename,
                containerId,
                url: previewUrl,
              },
            },
          });
          const photoId = data?.uploadPhotoFromUrl?.id;
          if (photoId) {
            const { data } = await createMediaPhoto({
              variables: {
                payload: {
                  eventId,
                  photoId,
                },
              },
              ...(!collectionId && { refetchQueries: ['GetEventMedia'], awaitRefetchQueries: true }),
              update: cache => {
                if (eventHandle) {
                  evictMediaCollectionResponse({ cache, eventHandle: eventHandle });
                }
              },
            });
            const mediaId = data?.createMediaPhoto?.id;
            if (mediaId) {
              // if no collectionId, then no need to add media to collection
              if (collectionId) {
                telemetry.uploadAlbumPhoto(eventId, collectionId, mediaId, photoId, data.createMediaPhoto?.photo.url || '');
                await addMediaToCollection({
                  variables: {
                    payload: {
                      collectionId,
                      mediaId,
                    },
                  },
                  refetchQueries: ['MediaCollections', 'MediaCollectionById', 'GetEventMedia'],
                  awaitRefetchQueries: true,
                });
              }
              photoUploadStatusUpdate(false, 0);
            } else {
              toast(`${photosTranslationFn.savePhotoError()}`, { icon: <WarningTriangle /> });
            }
          } else {
            toast(`${photosTranslationFn.savePhotoError()}`, { icon: <WarningTriangle /> });
          }
        } catch {
          toast(`${photosTranslationFn.savePhotoError()}`, { icon: <WarningTriangle /> });
        } finally {
          photoUploadStatusUpdate(false, 0);
        }
      }
    },
  });

  return {
    openFilePicker,
    getPhotoPreviewUrl,
    getPhotosPrewiewUrls,
    fileUploadStatusData,
  };
};
